/**
 * Context:
 * This component is part of a high-conversion landing page.
 * Goal: maximize CTA clicks and clarity.
 * Constraints: no backend, no heavy libraries.
 * Design system defined in COPILOT.md (open this file when coding).
 */

import React, { useState, useEffect } from 'react';
import './App.css';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Phone, Mail, CheckCircle, ArrowRight, Clock, Users, Sparkles, Shield, Zap, TrendingUp, MessageSquare, FileText, Camera, Calendar, Star, X, BarChart, Target, Headphones } from 'lucide-react';
import VillaForm from './components/VillaForm';
import FaqDeleg from './components/ui/FaqDeleg';
import PulsiaBadge from './components/PulsiaBadge';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.6, ease: "easeOut" }
};

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: 0.8 }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.15
    }
  },
  viewport: { once: true }
};

function CounterAnimation({ end, duration = 2, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

function App() {
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [showVillaForm, setShowVillaForm] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -50]);

  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderVisible(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="App">
      {/* Header Navigation */}
      <motion.header
        className={`modern-header ${isHeaderVisible ? 'visible' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: isHeaderVisible ? 0 : -100 }}
        transition={{ duration: 0.3 }}>

        <div className="container-fluid">
          <div className="header-content-modern">
            <motion.div
              className="logo-container-modern"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}>

              <img
                src="/q7p6js7j_deleg.png"
                alt="Deleg.ai - Séjour de Rêve"
                className="logo-modern" />

            </motion.div>
            <motion.a
              onClick={() => setShowVillaForm(true)}
              className="btn-modern-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ cursor: 'pointer' }}>

              Réserver mon appel
            </motion.a>
          </div>
        </div>
      </motion.header>

      {/* Hero Section - Modern Asymmetric Layout */}
      <section className="hero-modern">
        <div className="container-fluid">
          <div className="hero-grid-modern">
            <motion.div
              className="hero-content-modern"
              style={{ y: heroY }}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}>

              <motion.div
                className="hero-badge-modern"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}>

                <Sparkles size={16} />
                <span>Une expertise locale au service des propriétaires de villas à La Réunion.</span>
              </motion.div>
              <h1 className="hero-title-modern">
                Votre villa a tout pour séduire.
                <span className="gradient-text">Nous structurons sa performance.</span>
              </h1>
              <div className="hero-slogan-modern">
                L’humain au cœur. <br />La technologie au service. <br />Vos locations saisonnières optimisées, sans stress.
              </div>
              <p className="hero-description-modern">
                Vous restez décisionnaire.<br />
                Nous structurons la visibilité, le suivi des réservations, les réponses voyageurs, l’organisation et le pilotage tarifaire pour réduire votre charge mentale et augmenter vos réservations.
              </p>
              <div className="hero-cta-modern">
                <motion.a
                  onClick={() => setShowVillaForm(true)}
                  className="btn-modern-large"
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 40px rgba(0,0,0,0.15)" }}
                  whileTap={{ scale: 0.95 }}
                  style={{ cursor: 'pointer' }}>

                  Planifier un échange téléphonique
                  <ArrowRight size={20} />
                </motion.a>
                <div className="hero-info-modern">
                  <CheckCircle size={18} />
                  <span>15 minutes • Expertise locale • Sans engagement</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="hero-image-modern"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}>

              <div className="hero-image-wrapper">
                <img src="https://images.pexels.com/photos/35043038/pexels-photo-35043038.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Villa luxueuse" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <motion.section
        className="stats-section-modern"
        {...fadeInUp}>

        <div className="container-fluid">
          <motion.div
            className="stats-grid-modern"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}>

            <motion.div className="stat-card-modern" variants={fadeInUp}>
              <div className="stat-icon-modern">
                <TrendingUp size={32} />
              </div>
              <div className="stat-value-modern">+
                <CounterAnimation end={+30} suffix="%" />
              </div>
              <div className="stat-label-modern">Taux d'occupation moyen</div>
            </motion.div>
            <motion.div className="stat-card-modern" variants={fadeInUp}>
              <div className="stat-icon-modern">
                <Clock size={32} />
              </div>
              <div className="stat-value-modern">
                <CounterAnimation end={24} suffix="/7" />
              </div>
              <div className="stat-label-modern">Disponibilité & réactivité imbatable grâce à l'IA</div>
            </motion.div>
            <motion.div className="stat-card-modern" variants={fadeInUp}>
              <div className="stat-icon-modern">
                <Users size={32} />
              </div>
              <div className="stat-value-modern">
                <CounterAnimation end={100} suffix="%" />
              </div>
              <div className="stat-label-modern">Des propriétaires satisfaits</div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Problem Section - Dark Background */}
      <section className="section-dark-modern">
        <div className="container-fluid">
          <motion.div className="section-header-center" {...fadeInUp}>
            <h2 className="section-title-modern">Ce que Deleg.ai change concrètement pour votre villa</h2>
            <p className="section-description-modern">
              Une organisation claire remplace le bricolage. Vous gagnez du temps, de la sérénité et une activité locative plus régulière.
            </p>
          </motion.div>
          <motion.div
            className="features-grid-modern"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}>

            <motion.div className="feature-card-modern" variants={fadeInUp}>
              <div className="feature-icon-modern">
                <Clock size={40} />
              </div>
              <h3 className="feature-title-modern">Charge mentale reduite</h3>
              <p className="feature-desc-modern">Vous n'avez plus a gerer seul les urgences, les relances et les details repetitifs de la location saisonniere.</p>
            </motion.div>
            <motion.div className="feature-card-modern" variants={fadeInUp}>
              <div className="feature-icon-modern">
                <MessageSquare size={40} />
              </div>
              <h3 className="feature-title-modern">Reponses plus rapides</h3>
              <p className="feature-desc-modern">Les demandes voyageurs recoivent une reponse claire et rapide, ce qui protege votre taux de conversion.</p>
            </motion.div>
            <motion.div className="feature-card-modern" variants={fadeInUp}>
              <div className="feature-icon-modern">
                <TrendingUp size={40} />
              </div>
              <h3 className="feature-title-modern">Meilleure visibilite</h3>
              <p className="feature-desc-modern">Votre annonce est mieux presentee, plus coherente et plus lisible pour les voyageurs qui comparent rapidement.</p>
            </motion.div>
            <motion.div className="feature-card-modern" variants={fadeInUp}>
              <div className="feature-icon-modern">
                <BarChart size={40} />
              </div>
              <h3 className="feature-title-modern">Moins de pertes de reservations</h3>
              <p className="feature-desc-modern">Calendrier, messages et suivi sont mieux coordonnes, ce qui limite les opportunites manqueees.</p>
            </motion.div>
            <motion.div className="feature-card-modern" variants={fadeInUp}>
              <div className="feature-icon-modern">
                <FileText size={40} />
              </div>
              <h3 className="feature-title-modern">Organisation plus rentable</h3>
              <p className="feature-desc-modern">Un cadre de travail simple et stable vous aide a piloter votre bien avec plus de regularite et de serenite.</p>
            </motion.div>
            <motion.div className="feature-card-modern" variants={fadeInUp}>
              <div className="feature-icon-modern">
                <Target size={40} />
              </div>
              <h3 className="feature-title-modern">Optimisation des revenus</h3>
              <p className="feature-desc-modern">Votre tarification est intelligente, dynamique et s'adapte en temps réel au marché, ce qui permet d'améliorer la rentabilité globale de votre villa.</p>
            </motion.div>
          </motion.div>
          <p className="section-description-modern" style={{ textAlign: 'center', marginTop: '2rem' }}>
            Une villa attractive mérite une organisation professionnelle.
          </p>
        </div>
      </section>

      {/* Solution Section - Asymmetric Layout */}
      <section className="solution-section-modern">
        <div className="container-fluid">
          <div className="solution-grid-modern">
            <motion.div className="solution-image-modern" {...fadeInUp}>
              <img src="https://images.pexels.com/photos/7641869/pexels-photo-7641869.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Consultation" />
            </motion.div>
            <motion.div className="solution-content-modern" {...fadeInUp}>
              <div className="badge-modern accent">Notre Solution</div>
              <h2 className="solution-title-modern">Notre promesse: une gestion claire, locale et rassurante</h2>
              <p className="solution-description-modern !text-lg">Deleg.ai met son expertise locale au service des propriétaires de villas à La Réunion qui veulent une solution sérieuse, humaine et organisée. Vous gardez les décisions, nous exécutons avec méthode.

              </p>
              <div className="solution-features-list">
                <div className="solution-feature-item">
                  <div className="solution-feature-icon"><Shield size={24} /></div>
                  <div>
                    <h4>Plus rassurant qu'une solution bricolee</h4>
                    <p>Chaque etape est formalisee: qui fait quoi, quand, et avec quel niveau de qualite.</p>
                  </div>
                </div>
                <div className="solution-feature-item">
                  <div className="solution-feature-icon"><Zap size={24} /></div>
                  <div>
                    <h4>Humain local + outils digitaux</h4>
                    <p>Une equipe basee a La Reunion, soutenue par des outils performants, pour rester reactive sans improviser.</p>
                  </div>
                </div>
                <div className="solution-feature-item">
                  <div className="solution-feature-icon"><Target size={24} /></div>
                  <div>
                    <h4>Objectif: regularite et rentabilite</h4>
                    <p>Visibilite, reponses, cadre administratif et suivi commercial sont pilotes dans la meme direction.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-light-modern">
        <div className="container-fluid">
          <motion.div className="section-header-center" {...fadeInUp}>
            <h2 className="section-title-modern">Pour quel type de proprietaire ?</h2>
            <p className="section-description-modern">
              Cette offre est pensee pour les proprietaires de La Reunion qui veulent deleguer intelligemment, sans se deconnecter des decisions importantes.
            </p>
          </motion.div>
          <motion.div
            className="steps-grid-modern"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}>

            <motion.div className="step-card-modern" variants={fadeInUp}>
              <h3 className="step-title-modern">Location déjà active ou avec fort potentiel</h3>
              <p className="step-desc-modern">Votre villa attire déjà des voyageurs ou possède un vrai potentiel touristique. L’objectif n’est pas de bricoler, mais de structurer et optimiser.</p>
            </motion.div>
            <motion.div className="step-card-modern" variants={fadeInUp}>
              <h3 className="step-title-modern">Villa avec piscine, 4 a 6 voyageurs minimum</h3>
              <p className="step-desc-modern">Un bien attractif qui merite une mise en marche commerciale serieuse et une qualite de reponse constante.</p>
            </motion.div>
            <motion.div className="step-card-modern" variants={fadeInUp}>
              <h3 className="step-title-modern">Proprietaire occupe ou a distance</h3>
              <p className="step-desc-modern">Vous manquez de temps pour suivre chaque demande, chaque relance et chaque ajustement tarifaire au bon moment.</p>
            </motion.div>
            <motion.div className="step-card-modern" variants={fadeInUp}>
              <h3 className="step-title-modern">Recherche d'un cadre premium et local</h3>
              <p className="step-desc-modern">Vous voulez une solution fiable, basee a La Reunion, plus securisante qu'un systeme bricolé ou trop impersonnel.</p>
            </motion.div>
            <motion.div className="step-card-modern" variants={fadeInUp}>
              <h3 className="step-title-modern">Besoin d'une execution adaptee au marche reunionnais</h3>
              <p className="step-desc-modern">Saint-Gilles, Saint-Leu, Saint-Pierre ou l'Ouest: les attentes voyageurs varient selon les zones. La strategie doit suivre le terrain.</p>
            </motion.div>
            <motion.div className="step-card-modern" variants={fadeInUp}>
              <h3 className="step-title-modern">Recherche d’optimisation</h3>
              <p className="step-desc-modern">Votre villa fonctionne déjà ou possède un bon potentiel. L’objectif est maintenant d’améliorer l’organisation, la visibilité et la régularité des réservations.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-light-modern">
        <div className="container-fluid">
          <motion.div className="section-header-center" {...fadeInUp}>
            <h2 className="section-title-modern">Notre methode en 3 etapes</h2>
            <p className="section-description-modern">
              Une methode simple pour passer d'une gestion subie a une organisation claire et rentable.
            </p>
          </motion.div>
          <motion.div
            className="steps-grid-modern"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}>

            <motion.div className="step-card-modern" variants={fadeInUp}>
              <div className="step-number-modern">01</div>
              <h3 className="step-title-modern">Diagnostic proprietaire</h3>
              <p className="step-desc-modern">Nous analysons votre bien, vos objectifs et vos points de blocage pour definir un plan adapte.</p>
            </motion.div>
            <motion.div className="step-card-modern" variants={fadeInUp}>
              <div className="step-number-modern">02</div>
              <h3 className="step-title-modern">Mise en place structuree</h3>
              <p className="step-desc-modern">Nous mettons en place la visibilite, les routines de reponse et le cadre administratif. Vous validez les choix cles.</p>
            </motion.div>
            <motion.div className="step-card-modern" variants={fadeInUp}>
              <div className="step-number-modern">03</div>
              <h3 className="step-title-modern">Suivi continu et ajustements</h3>
              <p className="step-desc-modern">Nous suivons la performance, ajustons les actions et vous gardez une vision claire des resultats.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section - Cards Grid */}
      <section className="services-section-modern">
        <div className="container-fluid">
          <motion.div className="section-header-center" {...fadeInUp}>
            <h2 className="section-title-modern">Ce que Deleg.ai gere pour vous</h2>
            <p className="section-description-modern">
              Le coeur operationnel de votre location saisonniere, sans jargon et sans complexite inutile.
            </p>
          </motion.div>
          <motion.div
            className="services-grid-modern"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}>

            <motion.div className="service-card-modern" variants={fadeInUp} whileHover={{ y: -5 }}>
              <MessageSquare size={32} className="service-icon-modern" />
              <h3>Réponses aux messages</h3>
              <p>Des reponses rapides et coherentes pour limiter les demandes perdues.</p>
            </motion.div>
            <motion.div className="service-card-modern" variants={fadeInUp} whileHover={{ y: -5 }}>
              <FileText size={32} className="service-icon-modern" />
              <h3>Contrats & signatures</h3>
              <p>Un cadre administratif propre, rassurant et simple a suivre.</p>
            </motion.div>
            <motion.div className="service-card-modern" variants={fadeInUp} whileHover={{ y: -5 }}>
              <TrendingUp size={32} className="service-icon-modern" />
              <h3>Tarification dynamique</h3>
              <p>Des ajustements tarifaires pour proteger votre rentabilite selon la demande locale.</p>
            </motion.div>
            <motion.div className="service-card-modern" variants={fadeInUp} whileHover={{ y: -5 }}>
              <Zap size={32} className="service-icon-modern" />
              <h3>Automatisation & IA</h3>
              <p>Des outils utiles pour accelerer l'operationnel, avec supervision humaine locale.</p>
            </motion.div>
            <motion.div className="service-card-modern" variants={fadeInUp} whileHover={{ y: -5 }}>
              <Camera size={32} className="service-icon-modern" />
              <h3>Photos & vidéos pro</h3>
              <p>Une presentation qui valorise votre villa avec piscine et attire des voyageurs qualifies.</p>
            </motion.div>
            <motion.div className="service-card-modern" variants={fadeInUp} whileHover={{ y: -5 }}>
              <Calendar size={32} className="service-icon-modern" />
              <h3>Livret d'accueil digital</h3>
              <p>Un parcours voyageur mieux organise pour reduire les incomprehensions et les sollicitations inutiles.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section - Modern Card */}
      <section className="pricing-section-modern">
        <div className="container-fluid">
          <motion.div className="pricing-container-modern" {...fadeInUp}>
            <div className="pricing-badge-modern">Tarification transparente</div>
            <h2 className="pricing-title-modern">Simple, transparent et sans risques</h2>
            <p className="pricing-subtitle-modern">Pas de surprise, pas de frais cachés. Vous ne payez que si votre bien génère des réservations.</p>
            <div className="pricing-card-modern">
              <div className="pricing-main-modern">
                <div className="pricing-item-modern">
                  <div className="pricing-value-modern">À partir de <span className="pricing-range">12% à 18%</span></div>
                  <div className="pricing-label-modern">Commission sur les réservations</div>
                </div>
                <div className="pricing-plus-modern">+</div>
                <div className="pricing-item-modern">
                  <div className="pricing-value-modern small">59€</div>
                  <div className="pricing-label-modern">de frais de gestion minimum, uniquement si le logement n’est pas loué.</div>
                </div>
              </div>
              <div className="pricing-adjustment-note">
                <p><strong>📌 Le taux de commission est ajusté en fonction :</strong></p>
                <ul>
                  <li>De la taille de votre bien</li>
                  <li>De sa capacité d'accueil</li>
                  <li>De son attractivité sur le marché (localisation, prestations...)</li>
                </ul>
                <p><strong>👉 Ce point est toujours clarifié ensemble lors de l’appel découverte.</strong></p>
              </div>
              <div className="pricing-features-modern">
                <div className="pricing-feature-modern"><CheckCircle size={20} />Gestion complète des plateformes (Airbnb, Booking, etc.)</div>
                <div className="pricing-feature-modern"><CheckCircle size={20} />Réponses aux messages, appels, e-mails</div>
                <div className="pricing-feature-modern"><CheckCircle size={20} />Contrats & signatures électroniques</div>
                <div className="pricing-feature-modern"><CheckCircle size={20} />Tarification dynamique</div>
                <div className="pricing-feature-modern"><CheckCircle size={20} />Automatisation IA & présence 7j/7</div>
                <div className="pricing-feature-modern"><CheckCircle size={20} />Assistance humaine + accompagnement personnalisé</div>
              </div>
              <div className="pricing-note-modern">
                <div className="pricing-note-header">
                  <Shield size={20} />
                  <p><strong>Notre intérêt = votre succès.</strong></p>
                </div>
                <p>Plus vous louez, plus on gagne. Zéro risque inutile pour vous.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section-modern">
        <div className="container-fluid">
          <motion.div className="section-header-center" {...fadeInUp}>
            <h2 className="section-title-modern">Ce que disent nos clients</h2>
          </motion.div>
          <motion.div
            className="testimonials-grid-modern"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}>

            <motion.div className="testimonial-card-modern" variants={fadeInUp}>
              <div className="testimonial-stars-modern">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </div>
              <p className="testimonial-text-modern">
                "J'avais un appartement à Saint-Denis qui restait vide la moitié de l'année. Depuis que Séjour de Rêve s'en occupe, mon taux d'occupation est passé à 85%. Je ne m'occupe plus de rien, c'est un vrai soulagement."
              </p>
              <div className="testimonial-author-modern">
                <div className="testimonial-avatar-modern" style={{ backgroundImage: 'url(https://images.pexels.com/photos/7579211/pexels-photo-7579211.jpeg?auto=compress&cs=tinysrgb&w=150)' }}></div>
                <div>
                  <div className="author-name-modern">Marie Leroy</div>
                  <div className="author-role-modern">Propriétaire à Saint-Denis</div>
                </div>
              </div>
            </motion.div>
            <motion.div className="testimonial-card-modern" variants={fadeInUp}>
              <div className="testimonial-stars-modern">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </div>
              <p className="testimonial-text-modern">
                "Je suis chef d'entreprise, je n'ai pas le temps de répondre aux messages à 22h. L'équipe est ultra réactive et professionnelle. Les contrats sont nickel, tout est automatisé."
              </p>
              <div className="testimonial-author-modern">
                <div className="testimonial-avatar-modern" style={{ backgroundImage: 'url(https://images.pexels.com/photos/7641869/pexels-photo-7641869.jpeg?auto=compress&cs=tinysrgb&w=150)' }}></div>
                <div>
                  <div className="author-name-modern">Jean-Baptiste Payet</div>
                  <div className="author-role-modern">Propriétaire à Saint-Gilles</div>
                </div>
              </div>
            </motion.div>
            <motion.div className="testimonial-card-modern" variants={fadeInUp}>
              <div className="testimonial-stars-modern">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </div>
              <p className="testimonial-text-modern">
                "Première location saisonnière pour moi. Séjour de Rêve a tout mis en place en quelques jours : photos pro, annonces optimisées, contrats... Mes premières réservations sont arrivées en une semaine !"
              </p>
              <div className="testimonial-author-modern">
                <div className="testimonial-avatar-modern" style={{ backgroundImage: 'url(https://images.pexels.com/photos/34519413/pexels-photo-34519413.jpeg?auto=compress&cs=tinysrgb&w=150)' }}></div>
                <div>
                  <div className="author-name-modern">Sophie Cadet</div>
                  <div className="author-role-modern">Propriétaire à Saint-Pierre</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <motion.section className="cta-section-modern" {...fadeInUp}>
        <div className="container-fluid">
          <div className="cta-content-modern">
            <h2 className="cta-title-modern">Prêt à déléguer ?</h2>
            <p className="cta-description-modern">
              Echangeons 15 minutes sur votre villa, vos objectifs et vos contraintes. Vous repartez avec une vision claire des prochaines etapes, meme si nous ne travaillons pas ensemble.
            </p>
            <motion.a
              onClick={() => setShowVillaForm(true)}
              className="btn-modern-large"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ cursor: 'pointer' }}>

              Demander mon echange
              <ArrowRight size={20} />
            </motion.a>
            <div className="cta-features-modern">
              <div className="cta-feature-modern"><Clock size={18} />15 minutes</div>
              <div className="cta-feature-modern"><CheckCircle size={18} />Simple et concret</div>
              <div className="cta-feature-modern"><Shield size={18} />Sans engagement</div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Villa Registration Form Modal */}
      {showVillaForm && <VillaForm isOpen={showVillaForm} onClose={() => setShowVillaForm(false)} />}

      {/* ====== FAQ (BAS DE PAGE) ====== */}
      <FaqDeleg />

      {/* Footer */}
      <footer className="footer-modern">
        <div className="container-fluid">
          <div className="footer-content-modern">
            <div className="footer-brand-modern">
              <div className="footer-logo-container">
                <img
                  src="/q7p6js7j_deleg.png"
                  alt="Deleg.ai - Séjour de Rêve"
                  className="footer-logo-modern" />

              </div>
              <div className="footer-slogan-modern">
                L'humain au cœur. La tech au service. Vos locations courtes durée simplifiées.
              </div>
              <p className="footer-description-modern">
                Nous concevons et opérons tout ce qu’il faut pour une location courte durée professionnelle, afin d’alléger votre charge mentale au quotidien.
                <br></br><br></br>Deleg.ai™ est une solution proposée par <b><a href="https://pulsia.fr">Pulsia.ai™</a></b> en collaboration avec la <b><a href="https://sejour-de-reve.com"> conciergerie "Séjour de rêve"</a></b>.
              </p>
            </div>
            <div className="footer-links-modern">
              <h4>Contact</h4>
              <a href="mailto:hello@sejour-de-reve.com" className="footer-link-modern">
                <Mail size={18} />hello@sejour-de-reve.com
              </a>
              <a href="tel:+262692123456" className="footer-link-modern">
                <Phone size={18} />+262 262 79 00 14
              </a>
              <h4>Pages utiles</h4>
              <a href="/services/" className="footer-link-modern">Services pour proprietaires</a>
              <a href="/gestion-location-saisonniere-la-reunion/" className="footer-link-modern">Gestion location saisonniere La Reunion</a>
              <a href="/conciergerie-airbnb-la-reunion/" className="footer-link-modern">Conciergerie Airbnb La Reunion</a>
              <a href="/optimisation-annonce-airbnb-la-reunion/" className="footer-link-modern">Optimisation annonce Airbnb</a>
              <a href="/faq/" className="footer-link-modern">FAQ proprietaires</a>
              <a href="/contact/" className="footer-link-modern">Contact</a>
              <a href="/rentabiliser-villa-piscine-la-reunion/" className="footer-link-modern">Rentabiliser une villa avec piscine</a>
              <a href="/eviter-reservations-perdues-location-saisonniere-la-reunion/" className="footer-link-modern">Eviter les reservations perdues</a>
              <a href="/ameliorer-visibilite-location-courte-duree-la-reunion/" className="footer-link-modern">Ameliorer la visibilite locale</a>
              <a href="/pourquoi-reponse-rapide-airbnb-booking/" className="footer-link-modern">Pourquoi la reponse rapide compte</a>
              <a href="/mentions-legales-confidentialite/" className="footer-link-modern">Mentions legales & confidentialite</a>
              <a href="/conditions-generales-de-vente/" className="footer-link-modern">Conditions generales de vente</a>

            </div>
          </div>
          <div className="footer-bottom-modern">
            <p>© 2025 Séjour de Rêve • Conciergerie La Réunion • Tous droits réservés</p>
          </div>
        </div>
      </footer>
      <PulsiaBadge />
    </div>);

}

export default App;



